import React from "react";
import { Dialog } from "@mui/material";
import type { DialogProps } from "@mui/material/Dialog";
import type { SxProps, Theme } from "@mui/material/styles";

import designTokens from "../../styles/designTokens";

export type DSDialogSize = "sm" | "md" | "lg";

export interface DSDialogProps extends DialogProps {
  size?: DSDialogSize;
  tall?: boolean;
  paperSx?: SxProps<Theme>;
}

const dialogWidthBySize: Record<DSDialogSize, string> = {
  sm: designTokens.semantic.dialog.base.width.sm,
  md: designTokens.semantic.dialog.base.width.md,
  lg: designTokens.semantic.dialog.base.width.lg,
};

const toSxArray = (...items: Array<SxProps<Theme> | undefined>): SxProps<Theme> =>
  items.filter(Boolean) as SxProps<Theme>;

const baseBackdropSx: SxProps<Theme> = {
  backgroundColor: designTokens.semantic.dialog.base.backdrop,
};

export const DSDialog: React.FC<DSDialogProps> = ({
  size = "md",
  tall = false,
  paperSx,
  PaperProps,
  slotProps,
  scroll = "body",
  children,
  ...dialogProps
}) => {
  const basePaperSx: SxProps<Theme> = {
    width: dialogWidthBySize[size],
    maxWidth: designTokens.semantic.dialog.base.maxWidth,
    maxHeight: tall
      ? designTokens.semantic.dialog.base.tallMaxHeight
      : designTokens.semantic.dialog.base.maxHeight,
    backgroundColor: designTokens.semantic.dialog.base.background,
    color: designTokens.semantic.dialog.base.text,
    borderRadius: designTokens.semantic.dialog.base.radius,
    overflow: designTokens.semantic.dialog.base.overflow,
    boxShadow: designTokens.semantic.dialog.base.shadow,
  };

  const backdropSlot = slotProps?.backdrop;
  const mergedBackdropSlot =
    typeof backdropSlot === "function"
      ? backdropSlot
      : {
          ...backdropSlot,
          sx: toSxArray(
            baseBackdropSx,
            backdropSlot && "sx" in backdropSlot ? backdropSlot.sx : undefined,
          ),
        };

  return (
    <Dialog
      {...dialogProps}
      scroll={scroll}
      maxWidth={false}
      PaperProps={{
        ...PaperProps,
        sx: toSxArray(basePaperSx, PaperProps?.sx, paperSx),
      }}
      slotProps={{
        ...slotProps,
        backdrop: mergedBackdropSlot,
      }}
    >
      {children}
    </Dialog>
  );
};

export default DSDialog;
